import React, { useState, useEffect } from 'react';
import axios from 'axios';

const TestimonialsModal = ({ isOpen, onClose, initialData }) => {
  const [testimonialsData, setTestimonialsData] = useState({
    heading: "",
    subheading: "",
    testimonials: [],
    gradientColors: ['#0760ad', '#098a48', '#118a94', '#339166', '#339166']
  });

  const [activeTab, setActiveTab] = useState('main');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(null);

  useEffect(() => {
    const fetchCurrentData = async () => {
      if (isOpen && !initialData) {
        try {
          setIsLoading(true);
          const response = await fetch(`${import.meta.env.VITE_API_URL}/home/testimonials/`);
          if (response.ok) {
            const data = await response.json();
            setTestimonialsData({
              heading: data.heading || "",
              subheading: data.subheading || "",
              testimonials: data.testimonials || [],
              gradientColors: data.gradientColors || ['#0760ad', '#098a48', '#118a94', '#339166', '#339166']
            });
          }
        } catch (error) {
          console.error('Error fetching testimonials data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCurrentData();
  }, [isOpen, initialData]);

  useEffect(() => {
    if (initialData) {
      setTestimonialsData(initialData);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTestimonialsData(prev => ({ ...prev, [name]: value }));
  };

  const handleGradientColorChange = (index, value) => {
    const updatedColors = [...testimonialsData.gradientColors];
    updatedColors[index] = value;
    setTestimonialsData(prev => ({ ...prev, gradientColors: updatedColors }));
  };

  const handleTestimonialChange = (index, field, value) => {
    const updatedTestimonials = [...testimonialsData.testimonials];
    updatedTestimonials[index] = { ...updatedTestimonials[index], [field]: value };
    setTestimonialsData(prev => ({ ...prev, testimonials: updatedTestimonials }));
  };

  const addTestimonial = () => {
    setTestimonialsData(prev => ({
      ...prev,
      testimonials: [...prev.testimonials, { 
        quote: "",
        name: "",
        role: "",
        rating: 5,
        image: ""
      }]
    }));
  };

  const removeTestimonial = (index) => {
    const updatedTestimonials = [...testimonialsData.testimonials];
    updatedTestimonials.splice(index, 1);
    setTestimonialsData(prev => ({ ...prev, testimonials: updatedTestimonials }));
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('category', 'testimonial_images');

    try {
      setIsUploading(true);
      setCurrentUploadIndex(index);
      setUploadProgress(0);

      const response = await axios.post(`${import.meta.env.VITE_API_URL}/images/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });

      const imageUrl = response.data.image || response.data.image.url;
      handleTestimonialChange(index, 'image', imageUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      setCurrentUploadIndex(null);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/home/testimonials/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(testimonialsData)
      });

      if (response.ok) {
        const updatedData = await response.json();
        onClose();
      } else {
        console.error('Failed to update testimonials data');
        alert('Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error updating testimonials data:', error);
      alert('An error occurred while saving. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Loading testimonials data...</p>
        </div>
      </dialog>
    );
  }

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className="modal-box w-11/12 max-w-5xl max-h-[90vh] relative">
        <button onClick={onClose} className="btn btn-sm btn-circle absolute right-2 top-2">✕</button>
        <h3 className="font-bold text-lg mb-4">Edit Testimonials Section</h3>
        
        <div className="tabs tabs-boxed mb-6">
          <button 
            className={`tab ${activeTab === 'main' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('main')}
          >
            Main Content
          </button>
          <button 
            className={`tab ${activeTab === 'testimonials' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('testimonials')}
          >
            Testimonials ({testimonialsData.testimonials.length})
          </button>
       
        </div>
        
        <div className="overflow-y-auto max-h-[60vh] pr-2">
          {activeTab === 'main' && (
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Heading</span>
                </label>
                <input 
                  type="text" 
                  name="heading" 
                  value={testimonialsData.heading || ''} 
                  onChange={handleChange} 
                  className="input input-bordered w-full" 
                />
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Subheading</span>
                </label>
                <textarea 
                  name="subheading" 
                  value={testimonialsData.subheading || ''} 
                  onChange={handleChange} 
                  className="textarea textarea-bordered w-full h-32" 
                />
              </div>
            </div>
          )}
          
          {activeTab === 'testimonials' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-bold">Testimonials</h4>
                <button 
                  onClick={addTestimonial}
                  className="btn btn-sm btn-primary"
                >
                  Add Testimonial
                </button>
              </div>

              {testimonialsData.testimonials.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No testimonials added yet. Click "Add Testimonial" to create one.
                </div>
              ) : (
                testimonialsData.testimonials.map((testimonial, index) => (
                  <div key={index} className="card bg-base-100 shadow-sm p-4 relative mb-6">
                    <button 
                      onClick={() => removeTestimonial(index)}
                      className="btn btn-sm btn-circle btn-error absolute -top-2 -right-2 z-10"
                    >
                      ✕
                    </button>
                    
                    <h4 className="font-bold mb-3">Testimonial {index + 1}</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Name</span>
                        </label>
                        <input 
                          type="text" 
                          value={testimonial?.name || ''} 
                          onChange={(e) => handleTestimonialChange(index, 'name', e.target.value)} 
                          className="input input-bordered w-full" 
                        />
                      </div>
                      
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Role</span>
                        </label>
                        <input 
                          type="text" 
                          value={testimonial?.role || ''} 
                          onChange={(e) => handleTestimonialChange(index, 'role', e.target.value)} 
                          className="input input-bordered w-full" 
                        />
                      </div>
                    </div>
                    
                    <div className="form-control mb-4">
                      <label className="label">
                        <span className="label-text">Quote</span>
                      </label>
                      <textarea 
                        value={testimonial?.quote || ''} 
                        onChange={(e) => handleTestimonialChange(index, 'quote', e.target.value)} 
                        className="textarea textarea-bordered w-full h-24" 
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Rating (1-5)</span>
                        </label>
                        <select
                          value={testimonial?.rating || 5}
                          onChange={(e) => handleTestimonialChange(index, 'rating', parseInt(e.target.value))}
                          className="select select-bordered w-full"
                        >
                          {[1, 2, 3, 4, 5].map(num => (
                            <option key={num} value={num}>{num} Star{num !== 1 ? 's' : ''}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Image</span>
                        </label>
                        <div className="flex items-center gap-2">
                          {testimonial?.image && (
                            <div className="avatar">
                              <div className="w-12 rounded-full">
                                <img 
                                  src={testimonial.image} 
                                  alt="Testimonial" 
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'https://via.placeholder.com/48?text=Avatar';
                                  }}
                                />
                              </div>
                            </div>
                          )}
                          <input 
                            type="file" 
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, index)}
                            className="file-input file-input-bordered file-input-sm w-full"
                            disabled={isUploading && currentUploadIndex === index}
                          />
                          {isUploading && currentUploadIndex === index && (
                            <progress 
                              className="progress progress-primary w-20" 
                              value={uploadProgress} 
                              max="100"
                            ></progress>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          
        
        </div>
        
        <div className="modal-action mt-4">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            Save Changes
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default TestimonialsModal;